// Block J (UI-22): moving a task to another day of the week. ONE function for the drag and for the
// tap route ("Move to…"), so the two can never disagree (addendum 7).
//
// Fixed with it (BlockJTasks.test.js, failing first): the week's drop saved the move and then left
// the task on the old day on screen (nothing told the app to reload; onRescheduleTask was never
// passed), and an occurrence of a repeating task was PUT to /api/tasks/<id>-<timestamp>, an id that
// is not a task. A repeating task moves from its edit form, where you choose which occurrences.
import { api } from '../api/client';
import { messageFromError } from './apiError';
import { showErrorToast, showToast } from './toast';
import { isExternalCalendarTask } from './calendarUtils';
import { toWallClock } from './recurringEdit';
import { formatTaskDay } from './taskTime';

const sameDay = (a, b) => a.toDateString() === b.toDateString();

// Resolves true when the server confirmed the move.
export async function moveTaskToDay(task, toDate) {
  if (!task) return false;
  if (isExternalCalendarTask(task)) {
    showToast("Events from your calendar move in Google or Outlook, not here.", { type: 'info' });
    return false;
  }
  if (task.isRecurringInstance) {
    showToast('This task repeats. Open it and choose Edit to move it, then pick which occurrences change.', { type: 'info' });
    return false;
  }
  const fromDt = task.datetime ? new Date(task.datetime) : null;
  if (!fromDt || Number.isNaN(fromDt.getTime())) {
    showToast('This task has no time to move from. Open it and choose Edit to give it a date.', { type: 'info' });
    return false;
  }
  if (sameDay(fromDt, toDate)) return false;

  // The same time of day on the new day, and the same length.
  const newStart = new Date(toDate);
  newStart.setHours(fromDt.getHours(), fromDt.getMinutes(), 0, 0);
  const fromEnd = task.deadline ? new Date(task.deadline) : null;
  const length = fromEnd && !Number.isNaN(fromEnd.getTime()) ? fromEnd.getTime() - fromDt.getTime() : 3600000;
  const newEnd = new Date(newStart.getTime() + length);

  try {
    const res = await api(`/api/tasks/${task.id}`, {
      method: 'PUT',
      body: JSON.stringify({ datetime: toWallClock(newStart), deadline: toWallClock(newEnd), dueChange: { source: 'calendar_drag' } }),
    });
    const data = await res.json();
    if (!data?.task) throw new Error('The server did not confirm the move. Refresh to check.');
    window.dispatchEvent(new Event('aidey:tasksUpdated'));
    showToast(`Moved to ${toDate.toLocaleDateString([], { weekday: 'long' })} ${formatTaskDay(newStart)}.`, { type: 'success' });
    return true;
  } catch (err) {
    showErrorToast(messageFromError(err, 'That move was not saved.'));
    return false;
  }
}
